// client/src/pages/Auth/Login.jsx

import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useTelegram } from '../../hooks/useTelegram';
import { useNotification } from '../../hooks/useNotification'; // Import du hook de notification
import { isGoogleConfigured, loginWithGoogleCredential } from '../../api/authService';
import Button from '../../components/ui/Button';
import './Auth.css';

const Login = () => {
  const [credentials, setCredentials] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const googleBtnRef = useRef(null);
  const [googleReady, setGoogleReady] = useState(false);
  
  const { login, loginWithGoogle, isAuthenticated, error: authError, user } = useAuth();
  const { webApp, isTelegramWebApp } = useTelegram();
  const { success, error: showError } = useNotification(); // Renamed to avoid conflict
  const navigate = useNavigate();

  // Rediriger si déjà authentifié avec un VRAI compte — pas un invité
  // (sinon l'auto-invité empêche d'accéder aux formulaires connexion/inscription)
  useEffect(() => {
    if (isAuthenticated && !user?.isGuest) {
      navigate('/');
    }
  }, [isAuthenticated, user, navigate]);

  // Mettre à jour l'erreur si provenant du contexte d'authentification
  useEffect(() => {
    if (authError) {
      showError(authError); // Use showError instead of setError
    }
  }, [authError, showError]);

  // Gérer les changements de formulaire
  const handleChange = (e) => {
    const { name, value } = e.target;
    setCredentials({
      ...credentials,
      [name]: value
    });
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await login(credentials);
      success('Connexion réussie!'); // Notification de succès
      navigate('/');
    } catch (err) {
      showError(err.message || 'Erreur lors de la connexion'); // Use showError
    } finally {
      setLoading(false);
    }
  };

  // Bouton Google officiel (Google Identity Services) quand configuré
  useEffect(() => {
    let cancelled = false;
    if (!isGoogleConfigured() || !googleBtnRef.current) return;
    import('../../api/authService').then(async ({ renderGoogleButton }) => {
      const ok = await renderGoogleButton(googleBtnRef.current, (credential) => {
        // Le credential est reçu → connexion
        setLoading(true);
        loginWithGoogleCredential(credential)
          .then((response) => {
            if (response.success) {
              success('Connexion avec Google réussie !');
              navigate('/');
            }
          })
          .catch((err) => showError(err.message || 'Erreur Google'))
          .finally(() => setLoading(false));
      });
      if (!cancelled && ok) setGoogleReady(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Connexion avec Google (One Tap) — bouton personnalisé
  const handleGoogleLogin = async () => {
    setLoading(true);
    
    try {
      await loginWithGoogle();
      success('Connexion avec Google réussie!');
      navigate('/');
    } catch (err) {
      showError(err.message || 'Erreur lors de la connexion avec Google');
    } finally {
      setLoading(false);
    }
  };

  // Connexion avec Telegram
  const handleTelegramLogin = () => {
    if (webApp) {
      // Logique spécifique à Telegram
      const telegramUser = webApp.initDataUnsafe?.user;
      if (telegramUser) {
        // Ici, vous devriez envoyer ces informations à votre backend pour authentification
        console.log('Telegram user data:', telegramUser);
      } else {
        showError('Impossible de récupérer les données utilisateur de Telegram');
      }
    } else {
      showError('WebApp Telegram non disponible');
    }
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <div className="auth-header">
          <h1>Connexion</h1>
          <p>Entrez dans le monde d'Elemental Duelists</p>
        </div>
        
        <form className="auth-form" onSubmit={handleSubmit}>
          <div className="form-group">
            <label htmlFor="username">Nom d'utilisateur</label>
            <input
              type="text"
              id="username"
              name="username"
              value={credentials.username}
              onChange={handleChange}
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Mot de passe</label>
            <input
              type="password"
              id="password"
              name="password"
              value={credentials.password}
              onChange={handleChange}
              required
            />
          </div>
          
          <Button 
            type="primary"
            onClick={handleSubmit}
            disabled={loading}
            fullWidth
          >
            {loading ? 'Connexion en cours...' : 'Se connecter'}
          </Button>
        </form>
        
        <div className="auth-social">
          {/* Bouton officiel Google (GIS) — visible dès qu'il est chargés */}
          <div
            ref={googleBtnRef}
            style={{
              display: 'flex',
              justifyContent: 'center',
              minHeight: googleReady ? 0 : 0,
              margin: googleReady ? '0 0 8px' : 0,
            }}
          />
          {!googleReady && (
            <Button
              type="outline"
              onClick={isGoogleConfigured() ? handleGoogleLogin : () => showError('Connexion Google non configurée : ajoutez REACT_APP_GOOGLE_CLIENT_ID dans client/.env (voir README)')}
              disabled={loading}
              fullWidth
              className="google-button"
            >
              <img src="/google-icon.png" alt="Google" className="google-icon" onError={(e) => { e.target.style.display = 'none'; }} />
              {isGoogleConfigured() ? 'Se connecter avec Google' : 'Google — ID OAuth à créer (client/.env)'}
            </Button>
          )}
        </div>
        
        {isTelegramWebApp && (
          <Button 
            onClick={handleTelegramLogin} 
            className="telegram-auth-button"
            disabled={loading}
          >
            Se connecter avec Telegram
          </Button>
        )}
        
        <div className="auth-links">
          <p>
            Pas encore de compte ?{' '}
            <Link to="/auth/register">Créer un compte</Link>
          </p>
          <p>
            <Link to="/auth/reset-password">Mot de passe oublié ?</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;